import { useEffect, useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import DateRangePicker, { rangeQuery, rangeReady } from "@/components/DateRangePicker";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line,
  PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import {
  ShoppingBag, DollarSign, Wallet, TrendingDown, Users, MessageSquareWarning,
  Loader2, Banknote, Clock, PiggyBank, AlertTriangle, Scale, ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";

const PREV_LABEL = { today: "vs hier", week: "vs sem. préc.", month: "vs mois préc.", custom: "vs période préc." };
const LOWER_IS_BETTER = ["expenses", "outstanding"];

function Delta({ cmp, k, period, testid }) {
  const c = cmp?.[k];
  if (!c) return null;
  const p = c.change_pct;
  const up = p > 0, flat = p === null || p === 0;
  const good = flat ? null : (LOWER_IS_BETTER.includes(k) ? !up : up);
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const tone = good === null ? "bg-slate-100 text-slate-500" : good ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600";
  return (
    <span data-testid={`${testid}-delta`} title={`Période précédente : ${c.previous}`}
      className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
      <Icon className="w-3 h-3" />{p === null ? "—" : `${p > 0 ? "+" : ""}${p.toLocaleString("fr-FR")}%`}
      <span className="font-normal opacity-70 ml-0.5">{PREV_LABEL[period]}</span>
    </span>
  );
}

const num = (v) => Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 });

const PERIODS = [
  { key: "today", label: "Aujourd'hui" },
  { key: "week", label: "Cette semaine" },
  { key: "month", label: "Ce mois" },
  { key: "custom", label: "Personnalisée" },
];

const STATUS_COLORS = {
  Nouveau: "#3b82f6", Collecté: "#f59e0b", Traité: "#a855f7", Dispatché: "#06b6d4", Livré: "#10b981",
};

function Kpi({ icon: Icon, label, value, tone = "sky", testid, sub, delta }) {
  const tones = {
    sky: "bg-brand-50 text-brand-600", emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600", red: "bg-red-50 text-red-600",
    purple: "bg-purple-50 text-purple-600", slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border p-5 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {delta}
      </div>
      <div className="mt-4 text-2xl font-bold font-heading text-slate-900">{value}</div>
      <div className="text-sm text-slate-500 mt-0.5">{label}</div>
      {sub && (
        <div data-testid={`${testid}-averages`} className="mt-3 pt-3 border-t border-slate-100 space-y-1 text-xs text-slate-500">
          {sub.map(([k, v], i) => (
            <div key={k} data-testid={`${testid}-avg-${i}`} className="flex justify-between gap-2"><span>{k}</span><b className="text-slate-700">{v}</b></div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [range, setRange] = useState({ from: "", to: "" });
  const ready = rangeReady(period, range);

  useEffect(() => {
    if (!ready) { return; }
    setLoading(true);
    api.get(`/kpi/dashboard?${rangeQuery(period, range)}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [period, range, ready]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading">Tableau de bord</h1>
          <p className="text-slate-500 text-sm mt-1">Vue d'ensemble de votre activité</p>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-2">
        <div className="flex flex-wrap gap-1 bg-white border rounded-xl p-1">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              data-testid={`period-${p.key}`}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                period === p.key ? "bg-brand-600 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {period === "custom" && <DateRangePicker testid="dash-range" value={range} onChange={setRange} />}
        </div>
      </div>
      {period === "custom" && !ready && (
        <p data-testid="dash-range-hint" className="text-sm text-slate-500">Choisissez une date de début et une date de fin pour afficher les indicateurs.</p>
      )}

      {!ready ? null : loading || !data ? (
        <div className="h-64 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi testid="kpi-total-orders" icon={ShoppingBag} label="Commandes totales" value={data.total_orders} tone="sky" delta={<Delta cmp={data.comparison} k="total_orders" period={period} testid="kpi-total-orders" />}
              sub={[["Moyenne / jour", num(data.averages?.orders_per_day)], ["Moyenne / client", num(data.averages?.orders_per_client)]]} />
            <Kpi testid="kpi-revenue" icon={DollarSign} label="Chiffre d'affaires" value={money(data.revenue)} tone="emerald" delta={<Delta cmp={data.comparison} k="revenue" period={period} testid="kpi-revenue" />}
              sub={[["Moyenne / jour", money(data.averages?.revenue_per_day)], ["Moyenne / client", money(data.averages?.revenue_per_client)]]} />
            <Kpi testid="kpi-kilos" icon={Scale} label="Kilos traités" value={`${num(data.kilos)} kg`} tone="purple" delta={<Delta cmp={data.comparison} k="kilos" period={period} testid="kpi-kilos" />}
              sub={[["Moyenne / jour", `${num(data.averages?.kilos_per_day)} kg`], ["Moyenne / client", `${num(data.averages?.kilos_per_client)} kg`]]} />
            <Kpi testid="kpi-collected" icon={Wallet} label="Montants encaissés" value={money(data.collected)} tone="purple" delta={<Delta cmp={data.comparison} k="collected" period={period} testid="kpi-collected" />} />
            <Kpi testid="kpi-outstanding" icon={Clock} label="Reste à payer" value={money(data.outstanding)} tone="amber" delta={<Delta cmp={data.comparison} k="outstanding" period={period} testid="kpi-outstanding" />} />
            <Kpi testid="kpi-delivery" icon={Banknote} label="Frais de livraison" value={money(data.delivery_collected)} tone="sky" delta={<Delta cmp={data.comparison} k="delivery_collected" period={period} testid="kpi-delivery" />} />
            <Kpi testid="kpi-expenses" icon={TrendingDown} label="Dépenses" value={money(data.expenses)} tone="red" delta={<Delta cmp={data.comparison} k="expenses" period={period} testid="kpi-expenses" />} />
            <Kpi testid="kpi-net" icon={PiggyBank} label="Bénéfice net" value={money(data.net ?? (data.revenue - data.expenses))} tone={(data.net ?? (data.revenue - data.expenses)) >= 0 ? "emerald" : "red"} delta={<Delta cmp={data.comparison} k="net" period={period} testid="kpi-net" />} />
            <Kpi testid="kpi-clients" icon={Users} label="Clients" value={data.clients_count} tone="slate" />
            <Kpi testid="kpi-complaints" icon={MessageSquareWarning} label="Réclamations ouvertes" value={data.open_complaints} tone="red" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(data.by_status).map(([st, val]) => (
              <div key={st} data-testid={`status-count-${st}`} className="bg-white border rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[st] }} />
                  <span className="text-sm text-slate-500">{st}</span>
                </div>
                <div className="text-2xl font-bold font-heading mt-1">{val}</div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border rounded-2xl p-5">
              <h3 className="font-semibold text-slate-800 mb-4">Évolution (14 derniers jours)</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="ca" name="CA ($)" stroke="#0284C7" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="commandes" name="Commandes" stroke="#a855f7" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white border rounded-2xl p-5">
              <h3 className="font-semibold text-slate-800 mb-4">Répartition des statuts</h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={data.status_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                    {data.status_distribution.map((e) => (
                      <Cell key={e.name} fill={STATUS_COLORS[e.name]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border rounded-2xl p-5">
              <h3 className="font-semibold text-slate-800 mb-4">Chiffre d'affaires vs Dépenses (14 jours)</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <Tooltip />
                  <Bar dataKey="ca" name="CA ($)" fill="#1877D0" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="depenses" name="Dépenses ($)" fill="#F87171" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white border rounded-2xl p-5 flex flex-col justify-center">
              <h3 className="font-semibold text-slate-800 mb-4">Résultat de la période</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center"><span className="text-slate-500">Chiffre d'affaires</span><b>{money(data.revenue)}</b></div>
                <div className="flex justify-between items-center"><span className="text-slate-500">Encaissé</span><b>{money(data.collected)}</b></div>
                <div className="flex justify-between items-center"><span className="text-slate-500">Dépenses</span><b className="text-red-600">-{money(data.expenses)}</b></div>
                <div className="flex justify-between items-center border-t pt-3">
                  <span className="font-semibold">Bénéfice net</span>
                  <b data-testid="net-value" className={`text-lg ${(data.net ?? (data.revenue - data.expenses)) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {money(data.net ?? (data.revenue - data.expenses))}
                  </b>
                </div>
              </div>
            </div>
          </div>

          {data.unpaid_fixed?.count > 0 && (
            <a href="/expenses" data-testid="dash-unpaid-fixed" className="flex flex-wrap items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-2xl p-4 text-amber-800 hover:bg-amber-100 transition-colors">
              <span className="font-semibold flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Charges fixes non payées</span>
              <span className="text-sm font-semibold">{data.unpaid_fixed.count} · {money(data.unpaid_fixed.total)} → Voir les dépenses</span>
            </a>
          )}
          {data.unpaid_delivered && data.unpaid_delivered.count > 0 && (
            <div data-testid="unpaid-alerts" className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> Alertes impayés — commandes livrées à recouvrer
                </h3>
                <span className="text-sm font-semibold text-amber-800">
                  {data.unpaid_delivered.count} commande(s) · {money(data.unpaid_delivered.total)}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {data.unpaid_delivered.items.map((o) => (
                  <div key={o.id} data-testid={`unpaid-${o.order_number}`} className="bg-white border border-amber-200 rounded-xl p-3 flex items-center justify-between">
                    <div>
                      <div className="font-mono font-semibold text-brand-700">{o.order_number}</div>
                      <div className="text-xs text-slate-500">{o.client_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-400">Solde</div>
                      <div className="font-bold text-amber-700">{money(o.balance)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
